import { useAuthQuery } from './auth'
import { GetPassport, isPassportProcessingComplete, Passport } from './useGetPassport'

type GetPassportsByIdsProps = {
  ids: string[]
  setHasAiProcessingTimedOut: (hasAiProcessingTimedOut: boolean) => void
  configTimeout?: number
  refetchCondition?: (data: Passport | undefined) => boolean
  enabled?: boolean
}

export function useGetLatestPassportFromIds(props: GetPassportsByIdsProps) {
  const { isLoading, isFetching, data } = useAuthQuery(
    ['passportsByIds', props.ids],
    async () => await GetLatestPassportOfIds(props.ids),
    {
      enabled: props.enabled !== undefined ? props.enabled : props.ids.length > 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchInterval: ({
        state,
      }: {
        state: { dataUpdatedAt: number; data?: { data?: Passport }; dataUpdateCount: number }
      }) => {
        const configTimeout = props.configTimeout || 45000
        const elapsedTime = state.dataUpdatedAt > 0 ? Date.now() - state.dataUpdatedAt : 0
        const hasExceededMaxTime = state.dataUpdatedAt > 0 && elapsedTime > configTimeout

        if (hasExceededMaxTime) {
          props.setHasAiProcessingTimedOut(true)
          return false
        }

        if (isPassportProcessingComplete(state.data?.data)) {
          return false
        }
        return 5000
      },
    },
  )

  return {
    isLoadingPassport: isLoading,
    isFetchingPassport: isFetching,
    passport: data?.data,
  }
}

export async function GetLatestPassportOfIds(ids: string[]) {
  // Make the parallel requests to fetch passports by their IDs
  const requests = ids.map(id => GetPassport(id))
  const responses = await Promise.all(requests)
  // Filter out any undefined responses (in case some IDs were invalid)
  const validResponses = responses.filter(response => response && response.data)
  const passports = validResponses.reduce(
    (acc, response) => {
      if (response.data) {
        acc.data.push(response.data)
      }
      return acc
    },
    { data: [] as Passport[] },
  )

  const passportOrdered = passports.data.sort((a, b) => {
    const dateA = new Date(a.attributes.createdAt as string).getTime()
    const dateB = new Date(b.attributes.createdAt as string).getTime()
    return dateB - dateA // Sort by createdAt in descending order
  })

  const latestPassport = passportOrdered[0]
  return {
    data: latestPassport,
  }
}
